import type { InstancedBufferGeometry } from 'three';
import { CameraDirector } from '../CameraDirector.js';
import { Stage, type StageViewport } from '../Stage.js';
import { ViewClock } from '../ViewClock.js';
import { NebulaVeil } from '../background/NebulaVeil.js';
import { Starfield } from '../background/Starfield.js';
import { Conductor } from '../dev/Conductor.js';
import { StatsOverlay } from '../dev/StatsOverlay.js';
import { MandalaCore } from '../mandala/MandalaCore.js';
import { MandalaRings } from '../mandala/MandalaRings.js';
import { MirrorDriftView } from '../mirror/MirrorDriftView.js';
import type { MirrorVideoRequest } from '../mirror/MirrorVideoResolver.js';
import { RippleField, type RippleFieldOptions } from '../wave/RippleField.js';
import { GameplayWaveField } from '../gameplay/GameplayWaveField.js';

/**
 * Assembles the world and drives it from dummy time.
 *
 * The scene is four objects and a post chain. Every one of them animates from a
 * uniform write, so the per-frame CPU cost is a fixed handful of assignments no
 * matter how much is on screen — which is the property that has to hold before
 * the simulation is allowed to add work of its own.
 */

export interface RenderingPrototypeOptions {
  readonly canvas: HTMLCanvasElement;
  readonly starCount?: number;
  readonly rippleCount?: number;
  readonly showStats?: boolean;
  readonly statsParent?: HTMLElement;
  /**
   * The song whose music video Mirror Drift should show.
   *
   * Stands in for the loaded Song Pack. The shape matches what a `ManifestV1`
   * offers (`displayName` plus `extensions`), so the runtime will later pass the
   * manifest through unchanged — see `MirrorVideoResolver`.
   */
  readonly song?: MirrorVideoRequest;
  readonly mirrorCount?: number;
  readonly mirrorSeed?: number;
  /**
   * Whether mirrors begin playing the moment their video decodes.
   *
   * The runtime sets this false so the song and the mirrors can be started
   * together from one gesture; left true, a shard would already be several
   * seconds into the MV before the music began.
   */
  readonly mirrorAutoplay?: boolean;
  /**
   * Drive the ripple field from simulation data instead of its self-animating
   * prototype mode. The runtime sets this; the standalone prototype does not.
   */
  readonly chartDrivenRipples?: boolean;
  /**
   * Judge windows and hit radius, forwarded to the ripple field.
   *
   * The renderer does not decide these — it is told them, by whoever also tells
   * the simulation, so the timing arc and the drawn hit circle describe the
   * judging that will actually happen.
   */
  readonly judge?: RippleFieldOptions['judge'];
}

export interface RenderingDiagnostics {
  readonly running: boolean;
  readonly requestedRunning: boolean;
  readonly frameCount: number;
  readonly lastFrameAt: number | null;
  readonly contextLost: boolean;
  readonly pageHidden: boolean;
  /** Submitted instances, not proof that a device's GPU shaded visible pixels. */
  readonly renderedNotes: number;
  readonly viewport: StageViewport;
}

export class RenderingPrototype {
  readonly stage: Stage;
  readonly overlay: StatsOverlay | null;

  private readonly clock = new ViewClock();
  private readonly conductor = new Conductor();
  private readonly director = new CameraDirector();

  readonly mirrors: MirrorDriftView;
  readonly ripples: RippleField;
  /** Gameplay waves drawn in a style other than MANDALA. See Phase 8A. */
  readonly gameplayWaves: GameplayWaveField;

  private readonly nebula: NebulaVeil;
  private readonly stars: Starfield;
  private readonly rings: MandalaRings;
  private readonly mandala: MandalaCore;

  /** Runs at the start of every frame, before anything is updated or drawn. */
  private frameCallback: ((viewTime: number) => void) | null = null;
  /**
   * Runs after the world has updated and before it is drawn.
   *
   * This is where the Visual Art Engine steps and draws its layers. It is a
   * second hook rather than a reuse of `frameCallback` because the two happen at
   * different moments for different reasons: gameplay must advance before
   * anything reads it, and visual layers must settle after it and before the
   * frame is composited.
   */
  private visualCallback: ((viewTime: number, deltaTime: number) => void) | null = null;
  /** Runs after the stage has composited. See `setVisualAfterRender`. */
  private afterRenderCallback: (() => void) | null = null;
  /**
   * Bloom strength trim owned by the visual engine, multiplied into the scale
   * the world already computes.
   *
   * Strength only. Threshold and radius are fixed for this project and are not
   * reachable from here on purpose.
   */
  private visualBloomTrim = 1;
  /** Phase offset owned by the visual engine. Zero is the untouched frame. */
  private visualPhaseShift = 0;
  private frameHandle = 0;
  private running = false;
  // Lifecycle pauses do not revoke the owner's intent to run. Explicit stop()
  // does, so pageshow cannot restart a loop stopped for an offline export.
  private requestedRunning = false;
  private resizePending = false;
  private clockStarted = false;
  private pageHidden = false;
  private contextLost = false;
  private disposed = false;
  private frameCount = 0;
  private lastFrameAt: number | null = null;
  private readonly resizeObserver: ResizeObserver | null;
  private readonly visualViewport: VisualViewport | null;
  private readonly onResize = () => this.resizeForLifecycle();
  private readonly onVisibility = () => this.handleVisibility();
  private readonly onPageHide = () => {
    this.pageHidden = true;
    this.pauseLoop();
  };
  private readonly onPageShow = () => {
    this.pageHidden = false;
    this.resizeForLifecycle();
    this.resumeLoop();
  };
  private readonly onContextLost = (event: Event) => {
    // Opt into restoration. Without preventDefault() WebGL cannot restore this
    // canvas after mobile GPU eviction or a background/foreground transition.
    event.preventDefault();
    this.contextLost = true;
    this.pauseLoop();
  };
  private readonly onContextRestored = () => {
    this.contextLost = false;
    this.resizeForLifecycle();
    this.resumeLoop();
  };

  constructor(options: RenderingPrototypeOptions) {
    this.stage = new Stage({ canvas: options.canvas });

    const fieldRadius = this.stage.field.radius;

    this.nebula = new NebulaVeil();
    this.stars = new Starfield({ count: options.starCount ?? 6000 });
    this.rings = new MandalaRings();
    this.mandala = new MandalaCore({ radius: fieldRadius });
    this.ripples = new RippleField({
      count: options.rippleCount ?? 40,
      fieldRadius,
      ...(options.judge === undefined ? {} : { judge: options.judge }),
      ...(options.chartDrivenRipples === undefined
        ? {}
        : { chartDriven: options.chartDrivenRipples }),
    });
    // The styles a gameplay wave can wear that are not the mandala seal. Built
    // whether or not anything selects one: the meshes cost an allocation and
    // nothing else until a wave is written to them, and building lazily would
    // put a shader compile in the middle of a run.
    this.gameplayWaves = new GameplayWaveField({ fieldRadius });

    this.mirrors = new MirrorDriftView({
      // Six rather than the initial four: measured at 1080p the extra pair costs
      // ~0.43 ms of GPU time and two more draw calls, with no dropped frames
      // across six decoders. The headroom was there, so the brief's upper bound
      // is what ships.
      shardCount: options.mirrorCount ?? 6,
      fieldRadius,
      ...(options.mirrorSeed === undefined ? {} : { seed: options.mirrorSeed }),
      ...(options.mirrorAutoplay === undefined ? {} : { autoplay: options.mirrorAutoplay }),
    });

    this.stage.scene.add(
      this.nebula.object,
      this.stars.object,
      this.rings.object,
      this.mirrors.object,
      this.mandala.object,
      this.ripples.object,
      // The tappable centres draw from the same instance buffers as the seals,
      // so this is a second draw of one upload rather than a second data path.
      this.ripples.coreObject,
      this.gameplayWaves.object,
    );

    // Fire and forget: the world must start immediately whether or not an MV
    // exists, so nothing above waits on this.
    if (options.song !== undefined) {
      void this.mirrors.load(options.song);
    }

    this.stars.setPixelRatio(this.stage.renderer.getPixelRatio());

    this.overlay =
      options.showStats === false
        ? null
        : new StatsOverlay(options.statsParent ?? document.body);

    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibility);
    options.canvas.addEventListener('webglcontextlost', this.onContextLost);
    options.canvas.addEventListener('webglcontextrestored', this.onContextRestored);
    this.visualViewport = window.visualViewport;
    this.visualViewport?.addEventListener('resize', this.onResize);
    this.resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(this.onResize);
    this.resizeObserver?.observe(options.canvas);
  }

  start(): void {
    if (this.disposed) return;
    this.requestedRunning = true;
    if (this.resizePending) this.resize();
    this.resumeLoop();
  }

  stop(): void {
    this.requestedRunning = false;
    this.pauseLoop();
  }

  private pauseLoop(): void {
    this.running = false;
    if (this.frameHandle !== 0) cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
  }

  private resumeLoop(): void {
    if (this.disposed || this.running || !this.requestedRunning || this.contextLost ||
        this.pageHidden || document.hidden) return;
    if (this.clockStarted) this.clock.resume();
    else {
      this.clock.start();
      this.clockStarted = true;
    }
    this.running = true;
    this.loop();
  }

  /** Lifecycle notifications must not replace an offline owner's output size. */
  private resizeForLifecycle(): void {
    if (this.disposed) return;
    this.resizePending = true;
    if (this.requestedRunning) this.resize();
  }

  /** Explicit owner resize remains available while the live loop is stopped. */
  resize(): void {
    if (this.disposed || this.contextLost) return;
    this.stage.resize();
    this.stars.setPixelRatio(this.stage.renderer.getPixelRatio());
    this.resizePending = false;
  }

  diagnostics(): RenderingDiagnostics {
    return {
      running: this.running,
      requestedRunning: this.requestedRunning,
      frameCount: this.frameCount,
      lastFrameAt: this.lastFrameAt,
      contextLost: this.contextLost,
      pageHidden: this.pageHidden || document.hidden,
      renderedNotes: (this.ripples.object.geometry as InstancedBufferGeometry).instanceCount +
        Object.values(this.gameplayWaves.counts()).reduce((sum, count) => sum + count, 0),
      viewport: this.stage.viewport,
    };
  }

  /**
   * Installs a per-frame hook.
   *
   * The runtime uses this to advance the simulation from `MusicClock` before the
   * frame is drawn, without the view layer needing to know that a simulation or
   * a song exists.
   */
  setFrameCallback(callback: ((viewTime: number) => void) | null): void {
    this.frameCallback = callback;
  }

  /** Installs the visual engine's per-frame hook. See `visualCallback`. */
  setVisualCallback(callback: ((viewTime: number, deltaTime: number) => void) | null): void {
    this.visualCallback = callback;
  }

  /**
   * Installs a hook that runs once the frame is composited and on screen.
   *
   * A renderer that writes to a texture the frame samples has to do it here.
   * Writing during the update puts the write before the read inside one
   * frame, and the read of a just-written texture is undefined.
   */
  setVisualAfterRender(callback: (() => void) | null): void {
    this.afterRenderCallback = callback;
  }

  /** Multiplies the world's own bloom scale. 1 leaves the frame untouched. */
  setVisualBloomTrim(trim: number): void {
    this.visualBloomTrim = Number.isFinite(trim) && trim > 0 ? trim : 1;
  }

  /**
   * Offsets the world's animation phase, in radians.
   *
   * Added to the time the mandala and rings are sampled at, so a disturbance
   * shifts where the pattern is in its cycle without touching its geometry or
   * its parameters. Zero leaves every frame exactly as it was.
   */
  setVisualPhaseShift(radians: number): void {
    this.visualPhaseShift = Number.isFinite(radians) ? radians : 0;
  }

  /** One frame. Exposed so a headless check can step the world deterministically. */
  renderFrame(now = performance.now()): void {
    this.frameCount += 1;
    this.lastFrameAt = now;
    const time = this.clock.tick(now);
    this.frameCallback?.(time);
    const signals = this.conductor.sample(time);

    // The phase shift reaches only the two mandala objects. The starfield and
    // the nebula keep the world's own time so a disturbance reads as the pattern
    // moving against its surroundings rather than the whole frame jumping.
    const patternTime = time + this.visualPhaseShift;

    this.nebula.update(time, signals.breath);
    this.stars.update(time, signals.breath);
    this.rings.update(patternTime, signals.breath);
    this.mandala.update(patternTime, signals.breath, signals.pulse);
    this.ripples.update(time, signals.pulse);
    this.mirrors.update(this.clock.delta, time, this.stage.camera);

    // After the world, before the frame: visual layers read a settled scene and
    // may add their own objects to it, which the one render below then draws.
    this.visualCallback?.(time, this.clock.delta);

    this.stage.bloom.setStrengthScale(signals.bloom * this.visualBloomTrim);
    this.director.update(this.stage.camera, signals, this.stage.framingDistance);

    this.stage.render();
    // After compositing, so a renderer may write to a texture this frame read.
    this.afterRenderCallback?.();
    this.overlay?.update(now, this.clock.delta * 1000, this.stage.stats());
  }

  /**
   * Steps and draws the world at an explicit moment.
   *
   * The offline path. `renderFrame` reads `ViewClock`, which is wall time — the
   * right source for a game being watched and fatal for a film, because the
   * starfield, the nebula and the mandala's own rotation would then depend on
   * when the render happened to be started. Two renders of one performance
   * produced identical simulation state and visibly different frames until this
   * existed.
   *
   * Neither hook is called: gameplay is not advancing, and the visual engine is
   * stepped by the offline session at a fixed delta before this is reached.
   */
  renderAtTime(time: number, deltaTime: number): void {
    const signals = this.conductor.sample(time);

    const patternTime = time + this.visualPhaseShift;
    this.nebula.update(time, signals.breath);
    this.stars.update(time, signals.breath);
    this.rings.update(patternTime, signals.breath);
    this.mandala.update(patternTime, signals.breath, signals.pulse);
    this.ripples.update(time, signals.pulse);
    this.mirrors.update(deltaTime, time, this.stage.camera);

    this.stage.bloom.setStrengthScale(signals.bloom * this.visualBloomTrim);
    this.director.update(this.stage.camera, signals, this.stage.framingDistance);
    this.stage.render();
    // After compositing, so a renderer may write to a texture this frame read.
    this.afterRenderCallback?.();
  }

  private loop = (): void => {
    this.frameHandle = 0;
    if (!this.running) return;
    this.frameHandle = requestAnimationFrame(this.loop);
    this.renderFrame();
  };

  private handleVisibility(): void {
    if (document.hidden) {
      this.pauseLoop();
      return;
    }
    // Re-anchor rather than restart, so the world resumes its phase instead of
    // snapping back to the beginning.
    this.resizeForLifecycle();
    this.resumeLoop();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stop();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.visualViewport?.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
    this.stage.renderer.domElement.removeEventListener('webglcontextlost', this.onContextLost);
    this.stage.renderer.domElement.removeEventListener('webglcontextrestored', this.onContextRestored);
    this.nebula.dispose();
    this.stars.dispose();
    this.rings.dispose();
    this.mandala.dispose();
    this.ripples.dispose();
    this.mirrors.dispose();
    this.overlay?.dispose();
    this.stage.dispose();
  }
}
